/* eslint-disable max-lines */
import {
  CaptionScriptixDescendant,
  DocumentSubtitleSegmentV1
} from '@utils/types/editors/subtitles/subtitle.ts';
import {
  Editable,
  ReactEditor,
  RenderElementProps,
  Slate,
  withReact
} from 'slate-react';
import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { HistoryEditor, withHistory } from 'slate-history';
import {
  createEditor,
  Descendant,
  Editor,
  Node,
  Range,
  Transforms
} from 'slate';
import {
  isAtStartOfNode,
  withErrorHandling
} from '@utils/extensions/slate-editor-utils.ts';
import { useCaption } from '@utils/providers/caption-context.tsx';
import { RenderLeafProps } from 'slate-react/dist/components/editable';
import { getSelectedWordFromSelection } from '@utils/editors/get-selected-word-from-selection.ts';
import { useAudio } from '@utils/providers/audio-context.tsx';
import cn from 'classnames';
import { StartStopTimeInputs } from '@components/editors/caption-editor/body/start-stop-time-inputs.tsx';
import { CaptionSegmentEditingTools } from '@components/editors/caption-editor/body/caption-segment-editing-tools.tsx';
import { useCaptionEditorContext } from '@utils/providers/caption-editor-context.tsx';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import {
  useLeafCommentAdornments,
  LeafCommentIcon
} from '@components/editors/shared/comments/use-leaf-comment-adornments.tsx';

/* eslint-disable no-restricted-syntax -- Semantic caption editor colors (subtitle palette, playback cues, pill foregrounds on tinted backgrounds) must stay stable across themes */

type Props = {
  index: number;
  segment: DocumentSubtitleSegmentV1;
  onWordOutOfView: (
    ref: HTMLSpanElement | null,
    direction: 'up' | 'down'
  ) => void;
  onWordInView: () => void;
  filteredItems: DocumentSubtitleSegmentV1[];
  /**
   * Lock the Slate surface — no typing, no history, no focus. Used by
   * the split-view source pane so the original captions stay intact
   * while the translator edits the right side.
   */
  readOnly?: boolean;
  /**
   * The paired source segment for this row when this editor is the
   * translation pane. When present, the split/add/kebab tools are
   * hidden — segment count is source-owned; letting the translator add
   * or split segments drifts the count and shifts timings onto the
   * wrong rows.
   *
   * #2291 reverses #2250's "translation timings are read-only" rule:
   * timestamps on the translation side are now editable, so each
   * translation document owns its own start/stop. CPS is measured
   * against the translation's own duration. The export invariant that
   * motivated #2250 (translation always matches source timings) no
   * longer holds — translators who want to retime a slot do it in
   * place instead of jumping to the source pane.
   */
  sourceSegment?: DocumentSubtitleSegmentV1;
};

const LINE_LIMIT = 40;
const BLOCK_LIMIT = 80;

const ScriptixCaptionEditor = ({
  segment,
  onWordOutOfView,
  onWordInView,
  index,
  filteredItems,
  readOnly,
  sourceSegment
}: Props) => {
  const [editor] = useState(() =>
    withErrorHandling(withHistory(withReact(createEditor())))
  );
  const {
    setEditor,
    setClickedIndex,
    editorUpdateSegment,
    undo,
    redo,
    historyVersion,
    document: captionDocument
  } = useCaption();
  const { addEditor, removeEditor, trackModification } =
    useCaptionEditorContext();
  const segmentRef = useRef<HTMLDivElement | null>(null);
  const { theme } = useThemeContext();

  // Theme-aware colors
  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const borderColor = pick('rgb(229 231 235)', 'rgb(55 65 81)'); // gray-200 / gray-700
  const pastTextColor = pick('#000000', '#e5e5e5'); // black / neutral-200
  const futureTextColor = pick('#707070', '#a3a3a3'); // gray / neutral-400

  useEffect(() => {
    addEditor(index.toString(), editor);
    return () => {
      removeEditor(index.toString());
    };
  }, [index, editor]);

  // Undo/redo replace the document; show the restored text in place so focus
  // and the other segments are untouched. Only runs after undo/redo, never
  // while typing, so it can't race keystrokes. Reads the context document:
  // the `segment` prop arrives a render later through the virtualised list.
  useEffect(() => {
    if (!historyVersion || !segment) return;
    const restored = captionDocument?.document.find(s => s.id === segment.id);
    if (!restored) return;
    const text = restored.text ?? '';
    if (Node.string(editor) === text) return;
    HistoryEditor.withoutSaving(editor as HistoryEditor, () => {
      Transforms.insertText(editor, text, {
        at: { anchor: Editor.start(editor, []), focus: Editor.end(editor, []) }
      });
    });
  }, [historyVersion]);

  const initialValue = useMemo(() => {
    if (!segment) return [];
    return [
      {
        id: segment.id,
        start: segment.start,
        stop: segment.stop,
        position: segment.position ?? null,
        bold: segment.bold ?? false,
        italic: segment.italic ?? false,
        underlined: segment.underlined ?? false,
        strike: segment.strike ?? false,
        color: segment.color ?? '',
        children: [{ text: segment.text || '' }]
      } as CaptionScriptixDescendant
    ];
  }, [segment]);

  const { setTimeAndSegment } = useCaption();
  const { currentTime, setTime, linkedPlayback } = useAudio();
  const linkedRef = useRef(linkedPlayback);
  linkedRef.current = linkedPlayback;

  // Apply 50ms shift for visual highlighting consistency
  const currentTimeMs = currentTime * 1000;
  const shiftedTimeMs = currentTimeMs + 50;
  const isCurrentWord = segment
    ? shiftedTimeMs >= segment.start && shiftedTimeMs <= segment.stop
    : false;

  const [plainText, setPlainText] = useState<string>(segment?.text || '');
  useEffect(() => {
    setPlainText(segment?.text || '');
  }, [segment?.id]); // reset when segment changes

  const lines = useMemo(() => plainText.split('\n'), [plainText]);
  const lineLengths = useMemo(() => lines.map(l => l.length), [lines]);
  const blockChars = useMemo(
    () => plainText.replace(/\n/g, '').length,
    [plainText]
  );

  const handleSelect = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      if (!linkedRef.current) return;
      const selectedWordElement = getSelectedWordFromSelection(editor);
      if (selectedWordElement) {
        setTimeAndSegment(Number(selectedWordElement));
        setTime(selectedWordElement.start / 1000);
      }
    },
    [editor, setTimeAndSegment, setTime]
  );

  const spanStyle = useMemo(
    () => ({
      color: (() => {
        if (isCurrentWord) return '#0a8df8';
        if (segment.stop && segment.stop <= shiftedTimeMs) return pastTextColor;
        if (segment.start && segment.start > shiftedTimeMs)
          return futureTextColor;
        return 'unset';
      })(),
      // Text styling (bold, italic, etc.) is only shown in video preview, not in editor
      fontStyle: 'normal',
      textDecoration: 'none',
      fontWeight: 'normal',
      fontSize: '16px'
    }),
    [isCurrentWord, segment, shiftedTimeMs, pastTextColor, futureTextColor]
  );

  useEffect(() => {
    if (!linkedPlayback) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (isCurrentWord) {
          if (!entry.isIntersecting) {
            const direction =
              entry.rootBounds &&
              entry.boundingClientRect.top < entry.rootBounds.top
                ? 'up'
                : 'down';
            onWordOutOfView(segmentRef.current, direction);
          } else {
            onWordInView();
          }
        }
      },
      { root: null, threshold: 0.1 }
    );

    if (segmentRef.current) {
      observer.observe(segmentRef.current);
    }

    return () => {
      if (segmentRef.current) {
        observer.unobserve(segmentRef.current);
      }
    };
  }, [isCurrentWord, onWordOutOfView, onWordInView, linkedPlayback]);

  const handleItemClick = (seg: DocumentSubtitleSegmentV1) => {
    setClickedIndex(seg.id);
    if (linkedRef.current) {
      setTime(seg.start / 1000);
    }
  };

  const handleMouseUp = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => handleSelect(e),
    [handleSelect]
  );

  const handleChange = (children: Descendant[]) => {
    editorUpdateSegment(children);
    trackModification(editor);
    const text = Node.string(editor);
    setPlainText(text);
  };

  // #2250 — anchored comments per segment. The leaf renderer below
  // reads `commentAdornments` to paint the amber/emerald underline
  // and the superscript icon; the compose trigger lives inside
  // `CaptionSegmentEditingTools` now (stacked with the other row
  // buttons) so this file no longer owns the compose click handler.
  const commentAdornments = useLeafCommentAdornments(
    segment?.start,
    segment?.stop
  );

  const renderElement = useCallback(
    ({ attributes, children, element }: RenderElementProps) => {
      const { id, start, stop } = element as CaptionScriptixDescendant;

      return (
        <div ref={segmentRef}>
          <div className="flex items-start w-full" key={id}>
            <div
              className="flex justify-between h-full w-full"
              onClick={e => {
                // Stop the click from bubbling to the list, but DO NOT clear the
                // selection here: removeAllRanges() ran on every click inside a
                // segment and wiped the caret the user just placed, so clicking a
                // segment left no cursor ("can't select the next segment").
                e.stopPropagation();
              }}
            >
              <span
                ref={segmentRef}
                className={cn(
                  'flex cursor-text relative w-full',
                  'h-[92px]',
                  'overflow-y-hidden',
                  'box-border',
                  'transition-all duration-200'
                )}
                style={{ borderColor, outline: 'none', boxShadow: 'none' }}
                data-start={start}
                data-stop={stop}
                data-index={id}
                id={`caption-${id}`}
                onClick={() => handleItemClick(segment)}
              >
                {/* Start/Stop inputs - 1/4 (25%) - start aligned */}
                <div
                  className="select-none min-h-full flex items-stretch justify-start w-[26%] min-w-[132px] shrink-0"
                  contentEditable={false}
                  suppressContentEditableWarning
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                >
                  <StartStopTimeInputs
                    segment={segment}
                    filteredItems={filteredItems}
                    counters={{
                      lineLengths,
                      lineLimit: LINE_LIMIT,
                      blockChars,
                      blockLimit: BLOCK_LIMIT
                    }}
                    readOnly={readOnly}
                  />
                </div>

                {/* Editor content - 70% - center aligned */}
                <div
                  className="w-[70%] h-full overflow-y-auto break-words text-center flex justify-center items-center px-2"
                  {...attributes}
                >
                  {children}
                </div>

                {/* Editing tools — pinned to the top-right of the row.
                    The cluster stacks vertically (Plus, Split, kebab,
                    Comment) so it fits the narrow 4% column. #2250:
                    the tools cluster stays mounted for BOTH source
                    and translation rows so the Comment button is
                    reachable everywhere; count-changers (Plus, Split,
                    kebab) are hidden inside the tools component when
                    `hideCountTools` is set — segment count belongs
                    to the source and letting the translator add or
                    split would drift the row-index pairing. */}
                {!readOnly && (
                  <div
                    className="select-none flex items-start justify-end w-[4%]"
                    contentEditable={false}
                    suppressContentEditableWarning
                    // preventDefault keeps the editor focused and its caret intact
                    // when clicking a tool (e.g. Split splits at the caret).
                    onMouseDown={e => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onClick={e => e.stopPropagation()}
                    style={{
                      visibility: ReactEditor.isFocused(editor)
                        ? 'visible'
                        : 'hidden'
                    }}
                  >
                    <CaptionSegmentEditingTools
                      segment={segment}
                      filteredItems={filteredItems}
                      hideCountTools={!!sourceSegment}
                    />
                  </div>
                )}
              </span>
            </div>
          </div>
        </div>
      );
    },
    [
      filteredItems,
      segment,
      sourceSegment,
      lineLengths,
      blockChars,
      borderColor,
      readOnly
    ]
  );

  const renderLeaf = useCallback(
    ({ attributes, children }: RenderLeafProps) => {
      const combinedBg = commentAdornments.tint ?? spanStyle.color;
      const combinedShadow = commentAdornments.underlineColor
        ? `inset 0 -2px 0 0 ${commentAdornments.underlineColor}`
        : undefined;
      return (
        <span
          data-start={segment?.start}
          data-stop={segment?.stop}
          onMouseUp={handleMouseUp}
          style={{
            ...spanStyle,
            ...(commentAdornments.tint
              ? { backgroundColor: commentAdornments.tint }
              : {}),
            ...(combinedShadow ? { boxShadow: combinedShadow } : {})
          }}
          {...attributes}
        >
          {children}
          <LeafCommentIcon adornments={commentAdornments} />
        </span>
      );
      void combinedBg;
    },
    [spanStyle, segment, handleMouseUp, commentAdornments]
  );

  // Early return if segment is undefined
  if (!segment) return null;

  return (
    <Slate
      // Only remount when the caller swaps a different segment into
      // this slot. Timing values can drift during ruler adjustments
      // or Peaks round-tripping; remounting mid-type breaks selection
      // restoration on Firefox/Windows, which is the symptom behind
      // #2329 ("cannot continue editing without clicking Save"). The
      // Slate value only mirrors ``text`` — timing lives outside it,
      // so start/stop have no business in the identity.
      key={segment.id}
      editor={editor}
      initialValue={initialValue}
      onChange={handleChange}
    >
      <Editable
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : 0}
        aria-readonly={readOnly || undefined}
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        style={{
          outline: 'none',
          padding: '0em 0em',
          border: 'none',
          boxShadow: 'none'
        }}
        className="no-outline"
        onKeyDown={event => {
          if (readOnly) return;
          const ctrlOrMeta = event.ctrlKey || event.metaKey;
          if (ctrlOrMeta) {
            const k = event.key.toLowerCase();

            // Undo/redo always use the document history, which holds text
            // edits as well as insert/split/delete/timing, so Ctrl+Z undoes
            // the latest change wherever it was made.
            if (k === 'z' && !event.shiftKey) {
              event.preventDefault();
              event.stopPropagation();
              undo();
              return;
            }
            if ((k === 'z' && event.shiftKey) || k === 'y') {
              event.preventDefault();
              event.stopPropagation();
              redo();
              return;
            }
            switch (k) {
              // Ctrl/Cmd +, = and - stay with the browser (page zoom).
              case 'f':
              case 'arrowleft':
              case 'b':
              case 'i':
              case 'u':
              case 's':
                event.preventDefault();
                return;
            }
          } else if (event.key === 'ArrowLeft' || event.key === 'Backspace') {
            const { selection } = editor;
            const isCollapsedAtStart = isAtStartOfNode(editor);
            if (
              selection &&
              isCollapsedAtStart &&
              Range.isCollapsed(selection)
            ) {
              event.preventDefault();
            }
          } else if (event.key === 'Enter') {
            // keep your single-block policy: insert a newline
            event.preventDefault();
            editor.insertText('\n');
          }
        }}
        onFocus={() => {
          if (readOnly) return;
          setEditor(editor);
        }}
      />
    </Slate>
  );
};

export default ScriptixCaptionEditor;
